import React, { useState, useEffect, useCallback } from "react";
import styles from "./SessionsFormationList.module.sass";
import { useNavigate } from "react-router-dom"; 
import cn from "classnames";
import Card from "../../components/Card";
import Form from "../../components/Form";
import Filters from "../../components/Filters";
import Icon from "../../components/Icon";

import Table from "./Table";
import Settings from "./Settings";

import { 
  getSessionsFormation, 
  deleteSessionFormation, 
  duplicateSessionFormation
} from "../../services/sessions.service";

const SessionsFormationListTeacher = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [noResults, setNoResults] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    sorting: "Plus récents",
    statusFilter: "tous"
  });

  const handleStatusChange = (id, newStatus) => {
    const updatedSessions = sessions.map(session => 
      session.id === id ? { ...session, status: newStatus } : session
    );
    setSessions(updatedSessions);
  };

  // Suppression individuelle
  const handleDeleteSession = async (sessionId) => {
    try {
      await deleteSessionFormation(sessionId);
      setSessions(prev => prev.filter(session => session.id !== sessionId));
    } catch (error) {
      console.error('Erreur lors de la suppression de la session:', error);
      throw error;
    }
  };

  // Duplication individuelle
  const handleDuplicateSession = async (sessionId) => {
    try {
      const duplicatedSession = await duplicateSessionFormation(sessionId);
      setSessions(prev => [duplicatedSession, ...prev]);
    } catch (error) {
      console.error('Erreur lors de la duplication de la session:', error);
      throw error;
    }
  };

  const fetchSessions = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoResults(false);

    try {
      // Construire les filtres
      let filterParts = [];
      
      // Filtre de recherche
      if (search.trim()) {
        filterParts.push(`(title~'${search.toLowerCase().trim()}' || description~'${search.toLowerCase().trim()}')`);
      }
      
      // Filtre par statut
      if (filters.statusFilter !== "tous") {
        filterParts.push(`status="${filters.statusFilter}"`);
      }
      
      const filterString = filterParts.length > 0 ? filterParts.join(' && ') : null;
      
      // Déterminer le tri
      let sortString = "-created"; // Par défaut : plus récents
      if (filters.sorting === "Plus anciens") {
        sortString = "+created";
      } else if (filters.sorting === "Date de début") {
        sortString = "-start_date";
      }

      const result = await getSessionsFormation({
        page,
        perPage: 10,
        filter: filterString,
        sort: sortString
      });
      
      setSessions(result.items || []); 
      setTotalPages(result.totalPages || 1); 
      setNoResults(result.items.length === 0 && !!search);

    } catch (err) {
      if (err.isAbort || (err.response && err.response.code === 0)) {
        console.warn('Request was autocancelled:', err.message);
      } else {
        console.error("Erreur lors du chargement des sessions de formation:", err);
        setError("Impossible de charger les sessions de formation");
        setSessions([]);
        setTotalPages(1);
      }
    } finally {
      setLoading(false);
    }
  }, [page, search, filters]); 
  
  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  const handleSearch = (e) => {
    e?.preventDefault();
    setPage(1); 
    fetchSessions();
  };

  // Gestionnaire pour les changements de filtres
  const handleFiltersChange = (newFilters) => {
    console.log('Nouveaux filtres appliqués:', newFilters);
    setFilters(newFilters);
    setPage(1); // Remettre à la première page lors du changement de filtres
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  return (
    <Card 
      className={styles.card} 
      title="Sessions" 
      classTitle={cn("title-blue", styles.title)} 
      classCardHead={styles.head} 
      head={
        <>
          <Form
            className={styles.form}
            value={search}
            setValue={setSearch}
            onSubmit={handleSearch}
            placeholder="Rechercher par titre ou description"
            type="text"
            name="search"
            icon="search"
          />
          <Filters
            className={styles.filters}
            title="Filtrer"
          >
            <Settings onFiltersChange={handleFiltersChange} />
          </Filters>
        </>
      }
    >
      {error && <div className={styles.error}>{error}</div>}
      
      {loading ? (
        <div className={styles.loading}>Chargement...</div>
      ) : (
        <>
          <Table
            className={styles.table}
            sessions={sessions}
            loading={loading}
            error={error}
            noResults={noResults}
            page={page}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            onStatusChange={handleStatusChange}
            onDeleteSession={handleDeleteSession}
            onDuplicateSession={handleDuplicateSession}
          />
          <div className={styles.foot}>
            <button 
              className={styles.arrow} 
              onClick={() => handlePageChange(Math.max(1, page - 1))} 
              disabled={page === 1}
            >
              <Icon name="arrow-left" size="20" />
            </button>
            <span className={styles.pagination}>
              Page {page} sur {totalPages}
            </span>
            <button 
              className={styles.arrow} 
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))} 
              disabled={page === totalPages}
            >
              <Icon name="arrow-right" size="20" />
            </button>
          </div>
        </>
      )}
    </Card>
  );
};

export default SessionsFormationListTeacher;